import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import {
  newsletterApi,
  NewsletterCampaign,
  NewsletterTemplate,
} from '@/api/newsletter'
import Badge from '@/components/ui/Badge'
import {
  EnvelopeIcon,
  PaperAirplaneIcon,
  DocumentDuplicateIcon,
  TrashIcon,
  PlusIcon,
  ArrowPathIcon,
  ChartBarIcon,
  CalendarIcon,
  EyeIcon,
  PencilIcon,
} from '@heroicons/react/24/outline'

export default function AdminNewsletterPage() {
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<'campaigns' | 'templates' | 'digests'>('campaigns')
  const [showCampaignModal, setShowCampaignModal] = useState(false)
  const [showTemplateModal, setShowTemplateModal] = useState(false)
  const [editingCampaign, setEditingCampaign] = useState<Partial<NewsletterCampaign> | null>(null)
  const [editingTemplate, setEditingTemplate] = useState<Partial<NewsletterTemplate> | null>(null)

  // Queries
  const { data: campaignsData, isLoading: loadingCampaigns, refetch: refetchCampaigns } = useQuery({
    queryKey: ['newsletter-campaigns'],
    queryFn: () => newsletterApi.getCampaigns(),
  })

  const { data: templates, isLoading: loadingTemplates } = useQuery({
    queryKey: ['newsletter-templates'],
    queryFn: newsletterApi.getTemplates,
  })

  const { data: digests, isLoading: loadingDigests } = useQuery({
    queryKey: ['newsletter-digests'],
    queryFn: newsletterApi.getDigests,
  })

  const { data: stats } = useQuery({
    queryKey: ['newsletter-stats'],
    queryFn: newsletterApi.getCampaignStats,
  })

  const campaigns = campaignsData?.results || []

  // Mutations
  const createCampaignMutation = useMutation({
    mutationFn: newsletterApi.createCampaign,
    onSuccess: () => {
      toast.success('Campagna creata con successo')
      queryClient.invalidateQueries({ queryKey: ['newsletter-campaigns'] })
      setShowCampaignModal(false)
      setEditingCampaign(null)
    },
    onError: () => toast.error('Errore nella creazione della campagna'),
  })

  const updateCampaignMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<NewsletterCampaign> }) =>
      newsletterApi.updateCampaign(id, data),
    onSuccess: () => {
      toast.success('Campagna aggiornata')
      queryClient.invalidateQueries({ queryKey: ['newsletter-campaigns'] })
      setShowCampaignModal(false)
      setEditingCampaign(null)
    },
    onError: () => toast.error('Errore nell\'aggiornamento'),
  })

  const sendCampaignMutation = useMutation({
    mutationFn: newsletterApi.sendCampaign,
    onSuccess: (data) => {
      toast.success(data.detail || `Inviata a ${data.sent_count} destinatari`)
      queryClient.invalidateQueries({ queryKey: ['newsletter-campaigns'] })
    },
    onError: () => toast.error('Errore nell\'invio della campagna'),
  })

  const deleteCampaignMutation = useMutation({
    mutationFn: newsletterApi.deleteCampaign,
    onSuccess: () => {
      toast.success('Campagna eliminata')
      queryClient.invalidateQueries({ queryKey: ['newsletter-campaigns'] })
    },
    onError: () => toast.error('Errore nell\'eliminazione'),
  })

  const duplicateCampaignMutation = useMutation({
    mutationFn: newsletterApi.duplicateCampaign,
    onSuccess: () => {
      toast.success('Campagna duplicata')
      queryClient.invalidateQueries({ queryKey: ['newsletter-campaigns'] })
    },
    onError: () => toast.error('Errore nella duplicazione'),
  })

  const createTemplateMutation = useMutation({
    mutationFn: newsletterApi.createTemplate,
    onSuccess: () => {
      toast.success('Template creato')
      queryClient.invalidateQueries({ queryKey: ['newsletter-templates'] })
      setShowTemplateModal(false)
      setEditingTemplate(null)
    },
    onError: () => toast.error('Errore nella creazione del template'),
  })

  const deleteTemplateMutation = useMutation({
    mutationFn: newsletterApi.deleteTemplate,
    onSuccess: () => {
      toast.success('Template eliminato')
      queryClient.invalidateQueries({ queryKey: ['newsletter-templates'] })
    },
    onError: () => toast.error('Errore nell\'eliminazione'),
  })

  const handleSaveCampaign = () => {
    if (!editingCampaign) return

    if (editingCampaign.id) {
      updateCampaignMutation.mutate({ id: editingCampaign.id, data: editingCampaign })
    } else {
      createCampaignMutation.mutate(editingCampaign)
    }
  }

  const handleSaveTemplate = () => {
    if (!editingTemplate) return
    createTemplateMutation.mutate(editingTemplate)
  }

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'danger'> = {
      draft: 'default',
      scheduled: 'warning',
      sending: 'primary',
      sent: 'success',
      cancelled: 'danger',
    }
    const labels: Record<string, string> = {
      draft: 'Bozza',
      scheduled: 'Programmata',
      sending: 'In invio',
      sent: 'Inviata',
      cancelled: 'Annullata',
    }
    return <Badge variant={variants[status] || 'default'}>{labels[status] || status}</Badge>
  }

  // Stats Cards
  const statCards = [
    {
      title: 'Campagne Totali',
      value: stats?.total_campaigns || campaigns.length,
      icon: EnvelopeIcon,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
    },
    {
      title: 'Campagne Inviate',
      value: stats?.sent_campaigns || campaigns.filter(c => c.status === 'sent').length,
      icon: PaperAirplaneIcon,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
    },
    {
      title: 'Destinatari Totali',
      value: stats?.total_recipients || 0,
      icon: ChartBarIcon,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
    },
    {
      title: 'Tasso Apertura Medio',
      value: `${(stats?.avg_open_rate || 0).toFixed(1)}%`,
      icon: EyeIcon,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-100',
    },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestione Newsletter</h1>
          <p className="text-gray-600">Crea e gestisci campagne email e newsletter</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => refetchCampaigns()}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <ArrowPathIcon className="h-5 w-5" />
            Aggiorna
          </button>
          <button
            onClick={() => {
              setEditingCampaign({ name: '', subject: '', content_html: '', status: 'draft' })
              setShowCampaignModal(true)
            }}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <PlusIcon className="h-5 w-5" />
            Nuova Campagna
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {statCards.map((card) => (
          <div key={card.title} className="bg-white rounded-lg shadow-sm p-4">
            <div className={`inline-flex p-2 rounded-lg ${card.bgColor} mb-3`}>
              <card.icon className={`h-5 w-5 ${card.color}`} />
            </div>
            <p className="text-2xl font-bold text-gray-900">{card.value}</p>
            <p className="text-sm text-gray-500">{card.title}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          {[
            { id: 'campaigns', label: 'Campagne', icon: EnvelopeIcon },
            { id: 'templates', label: 'Template', icon: DocumentDuplicateIcon },
            { id: 'digests', label: 'Digest Programmati', icon: CalendarIcon },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="h-5 w-5" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Campaigns Tab */}
      {activeTab === 'campaigns' && (
        <div className="bg-white rounded-lg shadow-sm">
          {loadingCampaigns ? (
            <div className="p-8 text-center text-gray-500">Caricamento...</div>
          ) : campaigns.length === 0 ? (
            <div className="p-8 text-center">
              <EnvelopeIcon className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">Nessuna campagna creata</p>
              <button
                onClick={() => {
                  setEditingCampaign({ name: '', subject: '', content_html: '', status: 'draft' })
                  setShowCampaignModal(true)
                }}
                className="mt-4 text-primary-600 hover:text-primary-800 font-medium"
              >
                Crea la prima campagna
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Campagna</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stato</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Destinatari</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Aperture</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Click</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Azioni</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {campaigns.map((campaign) => (
                    <tr key={campaign.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-gray-900">{campaign.name}</p>
                          <p className="text-sm text-gray-500">{campaign.subject}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(campaign.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {campaign.recipients_count}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className="text-gray-900">{campaign.opened_count}</span>
                        {campaign.sent_count > 0 && (
                          <span className="text-gray-500"> ({campaign.open_rate?.toFixed(1) || 0}%)</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className="text-gray-900">{campaign.clicked_count}</span>
                        {campaign.sent_count > 0 && (
                          <span className="text-gray-500"> ({campaign.click_rate?.toFixed(1) || 0}%)</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {campaign.sent_at
                          ? new Date(campaign.sent_at).toLocaleDateString('it-IT')
                          : campaign.scheduled_at
                          ? `Prog: ${new Date(campaign.scheduled_at).toLocaleDateString('it-IT')}`
                          : new Date(campaign.created_at).toLocaleDateString('it-IT')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {campaign.status === 'draft' && (
                            <>
                              <button
                                onClick={() => {
                                  setEditingCampaign(campaign)
                                  setShowCampaignModal(true)
                                }}
                                className="p-1 text-gray-500 hover:text-gray-700"
                                title="Modifica"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => {
                                  if (confirm('Inviare la campagna?')) {
                                    sendCampaignMutation.mutate(campaign.id)
                                  }
                                }}
                                className="p-1 text-green-600 hover:text-green-800"
                                title="Invia"
                              >
                                <PaperAirplaneIcon className="h-4 w-4" />
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => duplicateCampaignMutation.mutate(campaign.id)}
                            className="p-1 text-blue-600 hover:text-blue-800"
                            title="Duplica"
                          >
                            <DocumentDuplicateIcon className="h-4 w-4" />
                          </button>
                          {campaign.status === 'draft' && (
                            <button
                              onClick={() => {
                                if (confirm('Eliminare questa campagna?')) {
                                  deleteCampaignMutation.mutate(campaign.id)
                                }
                              }}
                              className="p-1 text-red-600 hover:text-red-800"
                              title="Elimina"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-lg font-semibold">Template Email</h3>
            <button
              onClick={() => {
                setEditingTemplate({ name: '', subject_template: '', html_template: '', is_active: true })
                setShowTemplateModal(true)
              }}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              <PlusIcon className="h-5 w-5" />
              Nuovo Template
            </button>
          </div>
          {loadingTemplates ? (
            <div className="p-8 text-center text-gray-500">Caricamento...</div>
          ) : templates?.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              Nessun template creato
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {templates?.map((template) => (
                <div key={template.id} className="p-4 hover:bg-gray-50 flex justify-between items-center">
                  <div>
                    <p className="font-medium text-gray-900">{template.name}</p>
                    <p className="text-sm text-gray-500">Oggetto: {template.subject_template}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <Badge variant={template.is_active ? 'success' : 'default'}>
                      {template.is_active ? 'Attivo' : 'Inattivo'}
                    </Badge>
                    <button
                      onClick={() => {
                        if (confirm('Eliminare questo template?')) {
                          deleteTemplateMutation.mutate(template.id)
                        }
                      }}
                      className="p-1 text-red-600 hover:text-red-800"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Digests Tab */}
      {activeTab === 'digests' && (
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold">Digest Programmati</h3>
            <p className="text-sm text-gray-500">Newsletter automatiche ricorrenti</p>
          </div>
          {loadingDigests ? (
            <div className="p-8 text-center text-gray-500">Caricamento...</div>
          ) : digests?.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              Nessun digest programmato
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {digests?.map((digest) => (
                <div key={digest.id} className="p-4 hover:bg-gray-50">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-gray-900">{digest.name}</p>
                      <p className="text-sm text-gray-500">
                        Frequenza: {digest.frequency === 'daily' ? 'Giornaliera' : digest.frequency === 'weekly' ? 'Settimanale' : 'Mensile'}
                        {digest.send_day && ` &bull; Giorno: ${digest.send_day}`}
                        {digest.send_hour !== undefined && ` &bull; Ora: ${digest.send_hour}:00`}
                      </p>
                      <div className="flex gap-2 mt-2">
                        {digest.include_forum_highlights && <Badge variant="default">Forum</Badge>}
                        {digest.include_new_courses && <Badge variant="default">Corsi</Badge>}
                        {digest.include_upcoming_events && <Badge variant="default">Eventi</Badge>}
                        {digest.include_top_documents && <Badge variant="default">Documenti</Badge>}
                        {digest.include_leaderboard && <Badge variant="default">Classifica</Badge>}
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge variant={digest.is_active ? 'success' : 'default'}>
                        {digest.is_active ? 'Attivo' : 'Inattivo'}
                      </Badge>
                      {digest.next_send_at && (
                        <p className="text-xs text-gray-500 mt-1">
                          Prossimo invio: {new Date(digest.next_send_at).toLocaleDateString('it-IT')}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Campaign Modal */}
      {showCampaignModal && editingCampaign && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold">
                {editingCampaign.id ? 'Modifica Campagna' : 'Nuova Campagna'}
              </h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome campagna</label>
                <input
                  type="text"
                  value={editingCampaign.name || ''}
                  onChange={(e) => setEditingCampaign({ ...editingCampaign, name: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="Es. Newsletter Dicembre 2025"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Oggetto email</label>
                <input
                  type="text"
                  value={editingCampaign.subject || ''}
                  onChange={(e) => setEditingCampaign({ ...editingCampaign, subject: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="Es. Le novita del mese"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Template</label>
                <select
                  value={editingCampaign.template || ''}
                  onChange={(e) => setEditingCampaign({ ...editingCampaign, template: Number(e.target.value) || null })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="">Nessun template</option>
                  {templates?.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contenuto HTML</label>
                <textarea
                  value={editingCampaign.content_html || ''}
                  onChange={(e) => setEditingCampaign({ ...editingCampaign, content_html: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 h-40 font-mono text-sm"
                  placeholder="<html>...</html>"
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCampaignModal(false)
                  setEditingCampaign(null)
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Annulla
              </button>
              <button
                onClick={handleSaveCampaign}
                disabled={createCampaignMutation.isPending || updateCampaignMutation.isPending}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {createCampaignMutation.isPending || updateCampaignMutation.isPending ? 'Salvataggio...' : 'Salva'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Template Modal */}
      {showTemplateModal && editingTemplate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold">Nuovo Template</h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome template</label>
                <input
                  type="text"
                  value={editingTemplate.name || ''}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, name: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Oggetto template</label>
                <input
                  type="text"
                  value={editingTemplate.subject_template || ''}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, subject_template: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="Usa {first_name}, {last_name} per personalizzazione"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Template HTML</label>
                <textarea
                  value={editingTemplate.html_template || ''}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, html_template: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 h-40 font-mono text-sm"
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowTemplateModal(false)
                  setEditingTemplate(null)
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Annulla
              </button>
              <button
                onClick={handleSaveTemplate}
                disabled={createTemplateMutation.isPending}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {createTemplateMutation.isPending ? 'Salvataggio...' : 'Salva'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
